import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useState } from 'react';
import { UseFormReturn, useFieldArray, Controller } from 'react-hook-form';

import {
  ConstantArray,
  PathValue,
  Paths,
  CompanyUpdateApiDTO,
  Company,
  isServiceTitanCompany,
  CompanyCreateApiDTO
} from '_lib/api/apiTypes.shim';
import { Autocomplete } from '_lib/components/Autocomplete/Autocomplete';
import { Button } from '_lib/components/Button/Button';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { Checkbox } from '_lib/components/Checkbox/Checkbox';
import { Collapse } from '_lib/components/Collapse/Collapse';
import { Divider } from '_lib/components/Divider/Divider';
import { FieldSet } from '_lib/components/FieldSet/FieldSet';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FlexCard } from '_lib/components/FlexCard/FlexCard';
import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { FormHelperText } from '_lib/components/FormHelperText/FormHelperText';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';
import { PhoneNumberTextFieldFormItem, TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';

export const companyConfigZipZonesFormValues = [
  'callCenter.zipCodesServiced',
  'zones',
  'isZoned'
] satisfies ConstantArray<Paths<CompanyUpdateApiDTO | CompanyCreateApiDTO>>;

export type CompanyConfigZipZonesFormValue = (typeof companyConfigZipZonesFormValues)[number];

export type CompanyConfigZipZonesFormValues = {
  [K in CompanyConfigZipZonesFormValue]: PathValue<CompanyUpdateApiDTO | CompanyCreateApiDTO, K>;
};

/**
 * Form is only responsible for fields it controls, and we can check for the generic constraint in the
 * props in order for this to be reusable.
 *
 * See https://github.com/orgs/react-hook-form/discussions/8311#discussioncomment-9759698
 */
type CompanyConfigZipZonesFormProps<T extends CompanyConfigZipZonesFormValues> = {
  companyConfigZipZonesFormContext: T extends CompanyConfigZipZonesFormValues ? UseFormReturn<T> : never;
  company?: Pick<
    Partial<Company>,
    'isActive' | 'contactScheduleUrl' | 'primaryContact' | 'secondaryContact' | 'onCallNotes'
  > & {
    callCenter: Pick<
      Partial<Company['callCenter']>,
      'dispatchRules' | 'bigTicketItems' | 'bigTicketItemRules' | 'notes' | 'warrantyRules' | 'scheduleUrl'
    >;
    dataProvider: Company['dataProvider'];
  };
  onSetIsZoned?: (isZoned: boolean) => void;
};

export const CompanyConfigZipZonesForm = <T extends CompanyConfigZipZonesFormValues>(
  props: CompanyConfigZipZonesFormProps<T>
) => {
  const { companyConfigZipZonesFormContext, company, onSetIsZoned } = props;

  const confirmationDialog = useConfirmationDialog();

  const {
    control,
    watch,
    setValue,
    formState: { errors }
  } = companyConfigZipZonesFormContext;

  const isZoned = watch('isZoned');
  const zones = watch('zones') || [];
  const callCenterZipCodesServiced: string[] = watch('callCenter.zipCodesServiced') || [];

  const [cachedZones, setCachedZones] = useState<typeof zones>(zones);
  const [cachedCallCenterZipCodes, setCachedCallCenterZipCodes] = useState<string[]>(callCenterZipCodesServiced);

  const [expandedZones, setExpandedZones] = useState<Record<number, boolean>>({});

  const {
    fields: zoneFields,
    prepend: prependZone,
    remove: removeZone
  } = useFieldArray({
    control,
    name: 'zones'
  });

  const toggleZoneExpanded = (index: number) => {
    setExpandedZones((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleAddZone = () => {
    prependZone({
      title: '',
      zipCodes: [''] satisfies [string, ...string[]],
      contactScheduleUrl: company?.contactScheduleUrl || null,
      primaryContact: {
        name: company?.primaryContact?.name || null,
        role: company?.primaryContact?.role || null,
        phoneNumber: company?.primaryContact?.phoneNumber || null,
        email: company?.primaryContact?.email || null,
        notes: company?.primaryContact?.notes || null
      },
      secondaryContact: {
        name: company?.secondaryContact?.name || null,
        role: company?.secondaryContact?.role || null,
        phoneNumber: company?.secondaryContact?.phoneNumber || null,
        email: company?.secondaryContact?.email || null,
        notes: company?.secondaryContact?.notes || null
      },
      onCallNotes: company?.onCallNotes || null,
      callCenter: {
        dispatchRules: company?.callCenter?.dispatchRules || null,
        bigTicketItems: company?.callCenter?.bigTicketItems || null,
        bigTicketItemRules: company?.callCenter?.bigTicketItemRules || null,
        notes: company?.callCenter?.notes || null,
        warrantyRules: company?.callCenter?.warrantyRules || null,
        scheduleUrl: company?.callCenter?.scheduleUrl || null
      }
    });
  };

  const handleRemoveZone = async (index: number) => {
    const confirmRemove = await confirmationDialog.show({
      titleText: 'Do you want to delete this zone?',
      contentText: 'All zip codes for this zone will be removed from the company.'
    });
    if (confirmRemove?.action === 'CANCEL') return;

    const zone = zones[index];
    // Remove zone zip codes from the call center zip code array
    const updatedCallCenterZipCodes = callCenterZipCodesServiced.filter((zip) => !zone?.zipCodes?.includes(zip));
    setValue('callCenter.zipCodesServiced', updatedCallCenterZipCodes as never);

    removeZone(index);
    setExpandedZones((prev) => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
  };

  const handleToggleIsZoned = (checked: boolean) => {
    setValue('isZoned', checked);

    if (checked) {
      setCachedCallCenterZipCodes(callCenterZipCodesServiced);
      setValue('zones', cachedZones);

      const newZipCodesSet = new Set<string>();
      cachedZones.forEach((zone) => {
        zone.zipCodes.forEach((zip) => newZipCodesSet.add(zip));
      });
      setValue('callCenter.zipCodesServiced', Array.from(newZipCodesSet) as never);
    } else {
      setCachedZones(zones);
      setValue('zones', []);
      setValue('callCenter.zipCodesServiced', cachedCallCenterZipCodes as never);
    }
    onSetIsZoned?.(checked);
  };

  return (
    <Stack useFlexGap spacing={2} sx={{ width: '100%' }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={isZoned || false}
            onChange={(e) => handleToggleIsZoned(e.target.checked)}
            disabled={!!company && !company.isActive}
          />
        }
        label='Enable Zones'
        sx={{ width: 'fit-content' }}
      />
      {!isZoned && (
        <Controller
          name='callCenter.zipCodesServiced'
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={field.value || []}
              onChange={(_event, newValue) => field.onChange(newValue)}
              disabled={!!company && !company.isActive}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Zip Codes Serviced'
                  placeholder='Zip Codes Serviced'
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    errors?.['callCenter.zipCodesServiced']?.reduce?.((acc, curr) => acc + curr?.message, '')
                  }
                />
              )}
              fullWidth
            />
          )}
        />
      )}
      {isZoned && (
        <FlexCard>
          <PrimaryCardHeader
            title='Zones'
            action={
              <Tooltip title='Add Zone'>
                <IconButton
                  color='primaryInverted'
                  onClick={handleAddZone}
                  disabled={!!company && !company.isActive}
                  sx={{ marginRight: 0.5 }}>
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            }
          />
          {!zoneFields.length && (
            <Typography text='No zones added yet. Click "Add Zone" to add your first zone.' sx={{ padding: 2 }} />
          )}
          {zoneFields.map((field, index) => {
            const isExpanded = expandedZones[index] ?? false;
            return (
              <React.Fragment key={field.id}>
                {index > 0 && <Divider />}
                <Stack useFlexGap sx={{ padding: 2, width: '100%' }}>
                  <FlexBox sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Tooltip title='Remove Zone'>
                      <IconButton
                        size='small'
                        onClick={() => handleRemoveZone(index)}
                        disabled={!!company && !company.isActive}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </FlexBox>
                  <TextFieldFormItem
                    name={`zones.${index}.title`}
                    control={control}
                    label='Zone Title'
                    required
                    fullWidth
                    disabled={!!company && !company.isActive}
                  />
                  <Controller
                    name={`zones.${index}.zipCodes`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={field.value[0] === '' ? [] : field.value}
                        onChange={(_event, newValue) => field.onChange(newValue)}
                        disabled={!!company && !company.isActive}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Zip Codes Serviced'
                            placeholder='Zip Codes Serviced'
                            required
                            error={!!fieldState.error}
                            helperText={
                              fieldState.error?.message ||
                              errors?.zones?.[index]?.zipCodes?.reduce?.((acc, curr) => acc + curr?.message, '')
                            }
                          />
                        )}
                        fullWidth
                      />
                    )}
                  />
                  <FlexBox sx={{ justifyContent: 'center' }}>
                    <Button
                      variant='text'
                      onClick={() => toggleZoneExpanded(index)}
                      endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{ textTransform: 'none' }}>
                      {isExpanded ? 'Hide Details' : 'Show Details'}
                    </Button>
                  </FlexBox>
                  <Collapse in={isExpanded}>
                    <Stack useFlexGap spacing={2}>
                      <FieldSet legendText='Notes'>
                        <Stack useFlexGap sx={{ width: '100%' }}>
                          <TextFieldFormItem
                            name={`zones.${index}.callCenter.dispatchRules`}
                            control={control}
                            label='Dispatch Rules'
                            multiline
                            minRows={2}
                            maxRows={50}
                            fullWidth
                            disabled={!!company && !company.isActive}
                          />
                          <TextFieldFormItem
                            name={`zones.${index}.callCenter.bigTicketItems`}
                            control={control}
                            label='Big Ticket Items'
                            multiline
                            minRows={2}
                            maxRows={50}
                            fullWidth
                            disabled={!!company && !company.isActive}
                          />
                          <TextFieldFormItem
                            name={`zones.${index}.callCenter.bigTicketItemRules`}
                            control={control}
                            label='Big Ticket Item Scheduling Rules'
                            multiline
                            minRows={2}
                            maxRows={50}
                            fullWidth
                            disabled={!!company && !company.isActive}
                          />
                          <TextFieldFormItem
                            name={`zones.${index}.callCenter.warrantyRules`}
                            control={control}
                            label='Warranty Handling'
                            multiline
                            minRows={2}
                            maxRows={50}
                            fullWidth
                            disabled={!!company && !company.isActive}
                          />
                          <TextFieldFormItem
                            name={`zones.${index}.callCenter.notes`}
                            control={control}
                            label='Notes'
                            multiline
                            minRows={2}
                            maxRows={50}
                            fullWidth
                            disabled={!!company && !company.isActive}
                          />
                          <TextFieldFormItem
                            name={`zones.${index}.callCenter.scheduleUrl`}
                            control={control}
                            label='Schedule URL'
                            fullWidth
                            disabled={!!company && !company.isActive}
                          />
                          {!!company && !!company.dataProvider && isServiceTitanCompany(company) && (
                            <FormHelperText sx={{ paddingLeft: 1.75, marginTop: -1 }}>
                              Do not set a Schedule URL if call agent should use Service Titan Availability
                            </FormHelperText>
                          )}
                        </Stack>
                      </FieldSet>
                      <FieldSet legendText='Contacts'>
                        <FlexBox sx={{ gap: 3, width: '100%', flexDirection: { xs: 'column', md: 'row' } }}>
                          <FlexBox sx={{ flex: 1, flexDirection: 'column' }}>
                            <Typography variant='h3' text='Primary Contact' sx={{ paddingY: 1 }} />
                            <Stack useFlexGap sx={{ width: '100%' }}>
                              <TextFieldFormItem
                                name={`zones.${index}.primaryContact.name`}
                                control={control}
                                label='Name'
                                fullWidth
                                disabled={!!company && !company.isActive}
                              />
                              <TextFieldFormItem
                                name={`zones.${index}.primaryContact.role`}
                                control={control}
                                label='Role'
                                fullWidth
                                disabled={!!company && !company.isActive}
                              />
                              <PhoneNumberTextFieldFormItem
                                name={`zones.${index}.primaryContact.phoneNumber`}
                                control={control}
                                label='Phone Number'
                                disabled={!!company && !company.isActive}
                              />
                              <TextFieldFormItem
                                name={`zones.${index}.primaryContact.email`}
                                control={control}
                                label='Email'
                                fullWidth
                                disabled={!!company && !company.isActive}
                              />
                              <TextFieldFormItem
                                name={`zones.${index}.primaryContact.notes`}
                                control={control}
                                label='Contact Instructions'
                                multiline
                                minRows={2}
                                maxRows={50}
                                fullWidth
                                disabled={!!company && !company.isActive}
                              />
                            </Stack>
                          </FlexBox>
                          <FlexBox sx={{ flex: 1, flexDirection: 'column' }}>
                            <Typography variant='h3' text='Secondary Contact' sx={{ paddingY: 1 }} />
                            <Stack useFlexGap sx={{ width: '100%' }}>
                              <TextFieldFormItem
                                name={`zones.${index}.secondaryContact.name`}
                                control={control}
                                label='Name'
                                fullWidth
                                disabled={!!company && !company.isActive}
                              />
                              <TextFieldFormItem
                                name={`zones.${index}.secondaryContact.role`}
                                control={control}
                                label='Role'
                                fullWidth
                                disabled={!!company && !company.isActive}
                              />
                              <PhoneNumberTextFieldFormItem
                                name={`zones.${index}.secondaryContact.phoneNumber`}
                                control={control}
                                label='Phone Number'
                                disabled={!!company && !company.isActive}
                              />
                              <TextFieldFormItem
                                name={`zones.${index}.secondaryContact.email`}
                                control={control}
                                label='Email'
                                fullWidth
                                disabled={!!company && !company.isActive}
                              />
                              <TextFieldFormItem
                                name={`zones.${index}.secondaryContact.notes`}
                                control={control}
                                label='Contact Instructions'
                                multiline
                                minRows={2}
                                maxRows={50}
                                fullWidth
                                disabled={!!company && !company.isActive}
                              />
                            </Stack>
                          </FlexBox>
                        </FlexBox>
                      </FieldSet>
                      <FieldSet legendText='On Call Information'>
                        <Stack useFlexGap sx={{ width: '100%' }}>
                          <TextFieldFormItem
                            name={`zones.${index}.onCallNotes`}
                            control={control}
                            label='Instructions'
                            multiline
                            minRows={2}
                            maxRows={12}
                            fullWidth
                            disabled={!!company && !company.isActive}
                          />
                          <TextFieldFormItem
                            name={`zones.${index}.contactScheduleUrl`}
                            control={control}
                            label='Contact Schedule URL'
                            fullWidth
                            disabled={!!company && !company.isActive}
                          />
                        </Stack>
                      </FieldSet>
                    </Stack>
                  </Collapse>
                </Stack>
              </React.Fragment>
            );
          })}
        </FlexCard>
      )}
      {confirmationDialog.element}
    </Stack>
  );
};
